import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLoadCfTurnstileScript } from '@/components/CfCaptchaWidget';
import Dialog, { DialogContent, DialogTop } from '@/components/DialogSheet';
import ScrollArea from '@/components/ScrollArea';
import { useShallowEqualSelector } from '@/hooks';
import { useSetEditorNoneMode } from '@/hooks/editor';
import { useOauthErrors } from '@/hooks/oauth';
import { AUTH_MODAL_TYPES, closeAuthModal } from '@/store/profile';

import ChangePassword from './ChangePassword';
import Login from './Login';
import RecoverPassword from './RecoverPassword';
import SignUp from './SignUp';

function AuthModal() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { clearOauthError } = useOauthErrors();
	const setEditorNoneMode = useSetEditorNoneMode();

	const { open, type } = useShallowEqualSelector(state => ({
		open: state.profile.authModalOpen,
		type: state.profile.authModalType
	}));

	useLoadCfTurnstileScript(open);

	useEffect(
		() => () => {
			dispatch(closeAuthModal());
		},
		[]
	);

	useEffect(() => {
		if (open) {
			setEditorNoneMode();
		}
	}, [open]);

	const handleClose = () => {
		dispatch(closeAuthModal());
		clearOauthError();
	};

	const title = () => {
		if (
			[
				AUTH_MODAL_TYPES.EDITOR_SIGN_UP,
				AUTH_MODAL_TYPES.LOGIN,
				AUTH_MODAL_TYPES.SIGN_UP
			].includes(type)
		) {
			return t(`authModal.${type}.header`);
		}
		if (
			[
				AUTH_MODAL_TYPES.RECOVER_PASSWORD,
				AUTH_MODAL_TYPES.CHANGE_PASSWORD
			].includes(type)
		) {
			return t('common.password');
		}
	};

	const content = (
		<div className="h-[100%] w-full px-4 pb-[36px] pt-4 md:w-[397px] md:px-6 md:py-8">
			{type === AUTH_MODAL_TYPES.LOGIN && <Login />}
			{type === AUTH_MODAL_TYPES.SIGN_UP && <SignUp />}
			{type === AUTH_MODAL_TYPES.EDITOR_SIGN_UP && <SignUp isFastCheckout />}
			{type === AUTH_MODAL_TYPES.RECOVER_PASSWORD && <RecoverPassword />}
			{type === AUTH_MODAL_TYPES.CHANGE_PASSWORD && <ChangePassword />}
		</div>
	);

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				className="flex flex-col md:max-h-[calc(100vh-40px)] md:w-[397px]"
				onClose={handleClose}
				onOpenAutoFocus={event => event.preventDefault()}
			>
				<DialogTop title={title()} />
				<ScrollArea
					className="max-h-[calc(100svh-48px)] md:max-h-[calc(100%-48px)]"
					maxHeight="80svh"
				>
					{content}
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
}

export default AuthModal;
